import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ModalBodyClass } from '../modal-body';
import { Observable, Subscription } from 'rxjs';
import { DetailedModalComponent } from '../detailed-modal/detailed-modal.component';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../forms/ziro-forms-models';
import { ZiroSelectConfig } from '../../forms/fields/select/ziro-select.component';
import { ZiroRadioConfig } from '../../forms/fields/radio/ziro-radio.component';
import {
  AbstractMsRef,
  BulkJobStatus,
  CallingType,
  MicrosoftBulkJob,
  MicrosoftBulkJobUser,
  MicrosoftDialPlanFieldConfig,
  TeamsVoicePoliciesFieldConfig,
} from '../../shared/models/generated/ziroModels';
import { MicrosoftBulkProvisioningPollingService } from '../../shared/contexts/microsoft-bulk-provisioning-polling.service';
import { map, switchMap } from 'rxjs/operators';
import { CucmDnReportRow } from '../../migrations/migrations-cisco/migrations-cisco-directory-number-report/migrations-cisco-directory-numbers-report.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MicrosoftVoicePoliciesFieldConfigResource } from '../../helpdesk/shared/resources/microsoft-voice-policies-field-config.resource';
import { CommonModule } from '@angular/common';
import { ZiroFormsModule } from '../../forms/ziro-forms.module';
import { PipesModule } from '../../shared/pipes/pipes.module';

interface DnReportBulkJob {
  draftType: DraftType;
  jobName: string;
  existingBulkDraft: string;
  dialPlanGroup: string;
}

enum DraftType {
  NEW = 'NEW',
  EXISTING = 'EXISTING',
}

@Component({
  selector: 'ziro-migrations-bulk-job-migration-modal',
  templateUrl: 'migrations-bulk-job-migration-modal.component.html',
  styleUrls: ['migrations-bulk-job-migration-modal.component.scss'],
  providers: [MicrosoftBulkProvisioningPollingService],
  standalone: true,
  imports: [CommonModule, TranslateModule, ZiroFormsModule, PipesModule],
})
export class MigrationsBulkJobMigrationModalComponent extends ModalBodyClass implements OnInit, OnDestroy {
  @ViewChild('errorTemplate') errorTemplate: TemplateRef<any>;

  private _modalComponent = inject(DetailedModalComponent<any>);
  private _microsoftBulkProvisioningPollingService = inject(MicrosoftBulkProvisioningPollingService);
  private _translateService = inject(TranslateService);
  private _microsoftVoicePoliciesFieldConfigResource = inject(MicrosoftVoicePoliciesFieldConfigResource);

  isSubmitted = false;
  formConfig: ZiroFormConfig;

  private _dialPlans: MicrosoftDialPlanFieldConfig[] = [];
  private _bulkDrafts: MicrosoftBulkJob[] = [];
  private _users: CucmDnReportRow[] = [];
  private _subs = new Subscription();
  private _tenantId: number;

  ngOnInit(): void {
    this._dialPlans = this._modalComponent.modalViewProperties.dialPlans;
    this._bulkDrafts = this._modalComponent.modalViewProperties.bulkDrafts;
    this._users = this._modalComponent.modalViewProperties.users;
    this._tenantId = this._modalComponent.modalViewProperties.tenantId;
    this._microsoftBulkProvisioningPollingService.refresh(this._tenantId);

    this.entitySource.next({
      ...this.entity,
      draftType: DraftType.NEW,
    });

    this._initFormConfig();

    const validateAndSubmitSourceSub = this._validateAndSubmitSource.subscribe(() => {
      this.isSubmitted = true;
    });
    this._subs.add(validateAndSubmitSourceSub);

    const formUpdateSub = this.ziroFormsUpdate$.subscribe((data: ZiroFormsUpdate<DnReportBulkJob>) => {
      if (data?.new?.draftType !== data?.old?.draftType) {
        this._modalComponent.modalViewProperties.submitButtonLabel =
          data?.new?.draftType === DraftType.EXISTING
            ? 'tkey;migrations.cisco.directory_numbers_report.modal_form.action.update.label'
            : 'tkey;migrations.cisco.directory_numbers_report.modal_form.action.create.label';
      }

      if (data?.new?.existingBulkDraft !== data?.old?.existingBulkDraft) {
        const draft = this._bulkDrafts.find(
          (job: MicrosoftBulkJob) => job.id.toString() === data?.new?.existingBulkDraft,
        );
        const dialPlan = this._dialPlans.find((dpg: MicrosoftDialPlanFieldConfig) => dpg.id === draft.dialPlanId);

        this.entitySource.next({
          ...this.entity,
          dialPlanGroup: dialPlan?.id,
        });
      }
    });
    this._subs.add(formUpdateSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subs.unsubscribe();
  }

  submit(): Observable<{ draft: MicrosoftBulkJob; isExisting: boolean }> {
    if (this.entity.draftType === DraftType.EXISTING) {
      const draft: MicrosoftBulkJob = this._bulkDrafts.find(
        (job: MicrosoftBulkJob) => job.id.toString() === this.entity.existingBulkDraft.toString(),
      );
      const dialPlan: MicrosoftDialPlanFieldConfig = this._dialPlans.find(
        (dpg: MicrosoftDialPlanFieldConfig) => dpg.id === draft.dialPlanId,
      );

      return this._microsoftVoicePoliciesFieldConfigResource.get(this._tenantId, dialPlan.id, false).pipe(
        switchMap((voicePolicies: TeamsVoicePoliciesFieldConfig) => {
          const users: MicrosoftBulkJobUser[] = this._getUserRows(dialPlan, voicePolicies);
          const newBulkJob: MicrosoftBulkJob = {
            bulkJobName: draft.bulkJobName,
            dialPlanId: draft.dialPlanId,
            draftUsers: [...draft.draftUsers, ...users],
            completedUsers: [],
            endTime: null,
            failedUsers: [],
            id: draft.id,
            inProgressUsers: [],
            startTime: null,
            status: BulkJobStatus.DRAFT,
            tenantId: this._tenantId,
          };

          return this._microsoftBulkProvisioningPollingService.put(this._tenantId, draft.id, newBulkJob).pipe(
            map(() => {
              return {
                draft: {
                  ...newBulkJob,
                  id: draft.id,
                },
                isExisting: true,
              };
            }),
          );
        }),
      );
    } else {
      const dialPlan: MicrosoftDialPlanFieldConfig = this._dialPlans.find(
        (dpg: MicrosoftDialPlanFieldConfig) => dpg.id.toString() === this.entity.dialPlanGroup.toString(),
      );

      return this._microsoftVoicePoliciesFieldConfigResource.get(this._tenantId, dialPlan.id, false).pipe(
        switchMap((voicePolicies: TeamsVoicePoliciesFieldConfig) => {
          const users: MicrosoftBulkJobUser[] = this._getUserRows(dialPlan, voicePolicies);
          const newBulkJob: MicrosoftBulkJob = {
            bulkJobName: this.entity.jobName,
            dialPlanId: this.entity.dialPlanGroup,
            draftUsers: users,
            completedUsers: [],
            endTime: null,
            failedUsers: [],
            id: null,
            inProgressUsers: [],
            startTime: null,
            status: BulkJobStatus.DRAFT,
            tenantId: this._tenantId,
          };

          return this._microsoftBulkProvisioningPollingService.post(this._tenantId, newBulkJob).pipe(
            map((ref: AbstractMsRef) => {
              return {
                draft: {
                  ...newBulkJob,
                  id: ref.id,
                },
                isExisting: false,
              };
            }),
          );
        }),
      );
    }
  }

  private _getUserRows(
    dialPlan: MicrosoftDialPlanFieldConfig,
    voicePolicies: TeamsVoicePoliciesFieldConfig,
  ): MicrosoftBulkJobUser[] {
    return this._users.map((row: CucmDnReportRow): MicrosoftBulkJobUser => {
      // Follow this formatting or tests fail
      const notes: string = [
        `Directory Number:`,
        `  - Description: ${row.description || ''}`,
        `  - Alerting Name: ${row.dnAlertingName || ''}`,
        `  - Alerting Name ASCII: ${row.alertingNameAscii || ''}`,
        `  - Calling Search Space: ${row.callingSearchSpace || ''}`,
        `  - Route Partition: ${row.routePartition || ''}
        `,
        `Total Call Record Utilization:`,
        `  - Number Incoming Calls: ${row.totalIncomingCalls || ''}`,
        `  - Number Outgoing Calls: ${row.totalOutgoingCalls || ''}`,
        `  - Total Incoming Call Minutes: ${row.totalIncomingMinutes || ''}`,
        `  - Total Outgoing Call Minutes: ${row.totalOutgoingMinutes || ''}
        `,
        `Monthly PSTN Call Record Utilization:`,
        `  - Monthly PSTN Incoming Calls: ${row.monthlyPstnInboundCalls || ''}`,
        `  - Monthly PSTN Outgoing Calls: ${row.monthlyPstnOutgoingCalls || ''}`,
        `  - Monthly PSTN Incoming Call Minutes: ${row.monthlyPstnIncomingCallsDurationInMinutes || ''}`,
        `  - Monthly PSTN Outgoing Call Minutes: ${row.monthlyPstnOutgoingCallsDurationInMinutes || ''}
        `,
        `Associated Device:`,
        `  - Calling Search Space: ${row.associatedDeviceCss || ''}`,
        `  - Device Pool: ${row.devicePool || ''}`,
        `  - External Mask: ${row.externalMask || ''}`,
        `  - Line Display: ${row.lineDisplay || ''}`,
      ].join('\n');

      return {
        callingLineIdentityName: voicePolicies.callingLineIdentity.defaultValue,
        callingPolicyName: voicePolicies.callingPolicy.defaultValue,
        emergencyCallRoutingPolicyName: voicePolicies.emergencyCallRoutingPolicy.defaultValue,
        emergencyCallingPolicyName: voicePolicies.emergencyCallingPolicy.defaultValue,
        emergencyLocation: '',
        ipPhonePolicyName: voicePolicies.ipPhonePolicy.defaultValue,
        lineUri: this._fetchLineUri(dialPlan, row.teamsNumber),
        lineUriExtension: this._fetchLineUriExtension(dialPlan, row.teamsNumber),
        notes: notes,
        onlineVoiceRoutingPolicyName: voicePolicies.onlineVoiceRoutingPolicy.defaultValue,
        onlineVoicemailPolicyName: voicePolicies.onlineVoicemailPolicy.defaultValue,
        sharedCallRoutingPolicyName: voicePolicies.sharedCallingRoutingPolicy.defaultValue.policyName,
        teamsCallHoldPolicyName: voicePolicies.teamsCallHoldPolicy.defaultValue,
        teamsCallParkPolicyName: voicePolicies.teamsCallParkPolicy.defaultValue,
        tenantDialPlanName: voicePolicies.tenantDialPlan.defaultValue,
        userPrincipalName: row.teamsUser,
      };
    });
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        draftType: {
          dataAutomation: 'dn-report-draft-type',
          componentConfig: new ZiroRadioConfig({
            buttons: [
              {
                value: DraftType.NEW,
                label: 'tkey;migrations.cisco.directory_numbers_report.modal_form.new.label',
              },
              {
                value: DraftType.EXISTING,
                label: 'tkey;migrations.cisco.directory_numbers_report.modal_form.existing.label',
              },
            ],
            inline: false,
            isDisplayVertical: true,
          }),
        },
        jobName: {
          label: 'tkey;migrations.cisco.directory_numbers_report.modal_form.new.job_name.label',
          helpText: 'tkey;migrations.cisco.directory_numbers_report.modal_form.new.job_name.required.message',
          dataAutomation: 'dn-report-job-name',
          required: () => this.formData?.draftType === DraftType.NEW,
          hidden: () => this.formData?.draftType !== DraftType.NEW,
          valExcluded: () => this.formData?.draftType !== DraftType.NEW,
          validation: [
            {
              validator: (val: string): ZiroFormsValidationState => {
                if (!val) {
                  return ZiroFormsValidationState.VALID;
                }

                if (
                  this._bulkDrafts.some(
                    (draft: MicrosoftBulkJob) => draft.bulkJobName.toLowerCase() === val.toLowerCase(),
                  )
                ) {
                  return ZiroFormsValidationState.INVALID;
                }

                return ZiroFormsValidationState.VALID;
              },
              message: 'tkey;migrations.cisco.directory_numbers_report.modal_form.new.job_name.required.message',
            },
          ],
        },
        existingBulkDraft: {
          label: 'tkey;migrations.cisco.directory_numbers_report.modal_form.existing.bulk_draft.label',
          helpText: 'tkey;migrations.cisco.directory_numbers_report.modal_form.existing.bulk_draft.helptext',
          dataAutomation: 'dn-report-existing-bulk-draft',
          required: () => this.formData?.draftType === DraftType.EXISTING,
          hidden: () => this.formData?.draftType !== DraftType.EXISTING,
          componentConfig: new ZiroSelectConfig({
            options: [
              ...this._bulkDrafts
                .filter((draft) => draft.status === BulkJobStatus.DRAFT)
                .map((draft) => {
                  return {
                    label: `${draft.bulkJobName} - <strong>${draft.draftUsers.length} ${this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.modal_form.existing.bulk_draft.numbers_staged')}</strong>`,
                    value: draft.id,
                  };
                }),
            ],
            bindValue: 'value',
          }),
        },
        dialPlanGroup: {
          label: 'tkey;migrations.cisco.directory_numbers_report.modal_form.new.dpg.label',
          helpText: () =>
            this.formData?.draftType === DraftType.NEW
              ? 'tkey;migrations.cisco.directory_numbers_report.modal_form.new.dpg.helptext'
              : 'tkey;migrations.cisco.directory_numbers_report.modal_form.existing.dpg.disabled',
          dataAutomation: 'dn-report-dpg',
          required: () => true,
          hidden: () =>
            !this.formData?.draftType ||
            (this.formData?.draftType === DraftType.EXISTING && !this.formData.existingBulkDraft),
          disabled: () => this.formData?.draftType !== DraftType.NEW,
          componentConfig: new ZiroSelectConfig({
            options: [
              ...this._dialPlans.map((dpg) => {
                return {
                  label: dpg.name,
                  value: dpg.id,
                };
              }),
            ],
            bindValue: 'value',
          }),
        },
      },
    };
  }

  private _fetchLineUri(dialPlan: MicrosoftDialPlanFieldConfig, teamsNumber: string): string {
    if (dialPlan.callingType === CallingType.EXTENSION) {
      return dialPlan.mainNumber;
    }
    if (dialPlan.callingType === CallingType.SHARED_CALLING) {
      return null;
    }
    return teamsNumber;
  }

  private _fetchLineUriExtension(dialPlan: MicrosoftDialPlanFieldConfig, teamsNumber: string): string {
    if (dialPlan.callingType === CallingType.EXTENSION) {
      return teamsNumber;
    }
    if (dialPlan.callingType === CallingType.SHARED_CALLING) {
      return null;
    }
    return '';
  }
}
